import { Icon, Text } from "@shopify/polaris";
import {
    ChartVerticalFilledIcon,
    CreditCardIcon,
    DeleteIcon,
    ExitIcon,
    HomeIcon,
    PersonIcon,
    QuestionCircleIcon,
    MenuIcon,
    XIcon,
} from "@shopify/polaris-icons";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { UninstallModal } from "./UninstallModal";

interface LayoutProps {
    children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [uninstallModalOpen, setUninstallModalOpen] = useState(false);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setProfileDropdownOpen(false);
            }
            // Close mobile menu when clicking outside
            if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node) && mobileMenuOpen) {
                setMobileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [mobileMenuOpen]);

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    const navItems = [
        { label: "Dashboard", icon: HomeIcon, path: "/dashboard" },
        { label: "Reports", icon: ChartVerticalFilledIcon, path: "/standard-reports" },
        { label: "Pricing", icon: CreditCardIcon, path: "/pricing" },
        { label: "Help", icon: QuestionCircleIcon, path: "/help" },
    ];

    const isActive = (path: string) => {
        if (path === "/dashboard") {
            return location.pathname === "/dashboard" || location.pathname === "/";
        }
        if (path === "/standard-reports") {
            return (
                location.pathname === "/standard-reports" ||
                location.pathname.includes("/standard-reports")
            );
        }
        return location.pathname.startsWith(path);
    };

    const handleUninstall = () => {
        setUninstallModalOpen(true);
    };

    // Get user initials for avatar
    const getInitials = () => {
        if (user?.first_name) {
            return user.first_name.charAt(0).toUpperCase();
        }
        if (user?.email) {
            return user.email.charAt(0).toUpperCase();
        }
        return "U";
    };

    const getDisplayName = () => {
        if (user?.first_name && user?.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        if (user?.first_name) {
            return user.first_name;
        }
        return user?.email?.split("@")[0] || "User";
    };

    return (
        <div style={{ display: "flex", minHeight: "100vh", backgroundColor: "#f6f6f7" }}>
            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div
                    className="mobile-overlay"
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 99,
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                ref={sidebarRef}
                className={`sidebar ${mobileMenuOpen ? 'sidebar-open' : ''}`}
                style={{
                    width: "240px",
                    backgroundColor: "#ffffff",
                    borderRight: "1px solid #e1e3e5",
                    display: "flex",
                    flexDirection: "column",
                    position: "fixed",
                    height: "100vh",
                    zIndex: 100,
                    transform: mobileMenuOpen ? "translateX(0)" : undefined,
                    transition: "transform 0.3s ease",
                }}
            >
                {/* Logo */}
                <div
                    style={{
                        padding: "0 20px",
                        height: "64px",
                        borderBottom: "1px solid #e1e3e5",
                        backgroundColor: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <img
                        src="/icon.png"
                        alt="Analyzely"
                        loading="eager"
                        decoding="sync"
                        style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "6px",
                        }}
                    />
                    <Text as='span' variant='headingMd' fontWeight='bold'>
                        Analyzely 2.0
                    </Text>
                </div>

                {/* Navigation */}
                <nav style={{ flex: 1, padding: "12px 0" }}>
                    {navItems.map((item) => (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            style={{
                                display: "block",
                                width: "100%",
                                padding: "12px 20px",
                                border: "none",
                                backgroundColor: isActive(item.path) ? "#f1f2f4" : "transparent",
                                color: isActive(item.path) ? "#2c6ecb" : "#202223",
                                cursor: "pointer",
                                textAlign: "left",
                                fontSize: "14px",
                                fontWeight: isActive(item.path) ? 600 : 400,
                                transition: "background-color 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = "#f6f6f7";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive(item.path)) {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                }
                            }}
                        >
                            <span
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "12px",
                                }}
                            >
                                <Icon
                                    source={item.icon}
                                    tone={isActive(item.path) ? "info" : "base"}
                                />
                                {item.label}
                            </span>
                        </button>
                    ))}
                </nav>

                {/* Bottom actions */}
                <div style={{ borderTop: "1px solid #e1e3e5", padding: "12px 0" }}>
                    <button
                        onClick={() => navigate("/account")}
                        style={{
                            display: "block",
                            width: "100%",
                            padding: "12px 20px",
                            border: "none",
                            backgroundColor:
                                location.pathname === "/account" ? "#f1f2f4" : "transparent",
                            color: location.pathname === "/account" ? "#2c6ecb" : "#202223",
                            cursor: "pointer",
                            textAlign: "left",
                            fontSize: "14px",
                            fontWeight: location.pathname === "/account" ? 600 : 400,
                        }}
                        onMouseEnter={(e) => {
                            if (location.pathname !== "/account") {
                                e.currentTarget.style.backgroundColor = "#f6f6f7";
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (location.pathname !== "/account") {
                                e.currentTarget.style.backgroundColor = "transparent";
                            }
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                            <Icon
                                source={PersonIcon}
                                tone={location.pathname === "/account" ? "info" : "base"}
                            />
                            Account Settings
                        </span>
                    </button>
                    <button
                        onClick={handleUninstall}
                        style={{
                            display: "block",
                            width: "100%",
                            padding: "12px 20px",
                            border: "none",
                            backgroundColor: "transparent",
                            color: "#202223",
                            cursor: "pointer",
                            textAlign: "left",
                            fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f6f6f7";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                            <Icon source={DeleteIcon} />
                            Uninstall App
                        </span>
                    </button>
                    <button
                        onClick={logout}
                        style={{
                            display: "block",
                            width: "100%",
                            padding: "12px 20px",
                            border: "none",
                            backgroundColor: "transparent",
                            color: "#202223",
                            cursor: "pointer",
                            textAlign: "left",
                            fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#f6f6f7";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "12px" }}>
                            <Icon source={ExitIcon} />
                            Logout
                        </span>
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div className="layout-main-wrapper" style={{ flex: 1, marginLeft: "240px", display: "flex", flexDirection: "column" }}>
                {/* Header */}
                <header
                    style={{
                        backgroundColor: "#ffffff",
                        borderBottom: "1px solid #e1e3e5",
                        padding: "0 15px",
                        height: "64px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        position: "sticky",
                        top: 0,
                        zIndex: 50,
                    }}
                >
                    {/* Mobile Menu Button */}
                    <button
                        className="mobile-menu-btn"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        style={{
                            display: "none",
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "8px",
                            borderRadius: "8px",
                        }}
                    >
                        <Icon source={mobileMenuOpen ? XIcon : MenuIcon} />
                    </button>

                    {/* Spacer for desktop (pushes profile to right) */}
                    <div className="header-spacer" style={{ flex: 1 }} />

                    {/* User Profile Dropdown */}
                    <div ref={dropdownRef} style={{ position: "relative" }}>
                        <button
                            onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "8px",
                                borderRadius: "8px",
                                transition: "background-color 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#f6f6f7";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "transparent";
                            }}
                        >
                            <div style={{ textAlign: "right" }}>
                                <Text as='p' variant='bodySm' fontWeight='semibold'>
                                    {getDisplayName()}
                                </Text>
                                <Text as='p' variant='bodySm' tone='subdued'>
                                    {user?.email}
                                </Text>
                            </div>
                            <div
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    backgroundColor: "#5c6ac4",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                }}
                            >
                                {getInitials()}
                            </div>
                        </button>

                        {/* Dropdown Menu */}
                        {profileDropdownOpen && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: "100%",
                                    right: 0,
                                    marginTop: "4px",
                                    backgroundColor: "#ffffff",
                                    borderRadius: "8px",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                    border: "1px solid #e1e3e5",
                                    minWidth: "180px",
                                    overflow: "hidden",
                                    zIndex: 100,
                                }}
                            >
                                <button
                                    onClick={() => {
                                        navigate("/account");
                                        setProfileDropdownOpen(false);
                                    }}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        width: "100%",
                                        padding: "12px 16px",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        fontSize: "14px",
                                        color: "#202223",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#f6f6f7";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                    }}
                                >
                                    <span style={{ display: "flex", alignItems: "center" }}>
                                        <Icon source={PersonIcon} />
                                    </span>
                                    <span>Account Settings</span>
                                </button>
                                <div style={{ height: "1px", backgroundColor: "#e1e3e5" }} />
                                <button
                                    onClick={() => {
                                        logout();
                                        setProfileDropdownOpen(false);
                                    }}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "12px",
                                        width: "100%",
                                        padding: "12px 16px",
                                        border: "none",
                                        backgroundColor: "transparent",
                                        cursor: "pointer",
                                        textAlign: "left",
                                        fontSize: "14px",
                                        color: "#202223",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = "#f6f6f7";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                    }}
                                >
                                    <span style={{ display: "flex", alignItems: "center" }}>
                                        <Icon source={ExitIcon} />
                                    </span>
                                    <span>Logout</span>
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                {/* Page content */}
                <main style={{ flex: 1, padding: "24px" }}>{children}</main>

                {/* Footer */}
                <footer
                    style={{
                        padding: "16px 24px",
                        textAlign: "center",
                        borderTop: "1px solid #e1e3e5",
                        backgroundColor: "#ffffff",
                    }}
                >
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 16px",
                            backgroundColor: "#f6f6f7",
                            borderRadius: "8px",
                        }}
                    >
                        <Icon source={QuestionCircleIcon} tone='subdued' />
                        <Text as='span' variant='bodySm' tone='subdued'>
                            Need help? email us:{" "}
                            <a
                                href='mailto:care@analyzely.app'
                                style={{ color: "#2c6ecb", textDecoration: "none" }}
                            >
                                care@analyzely.app
                            </a>
                        </Text>
                    </div>
                </footer>
            </div>

            {/* Uninstall Modal */}
            <UninstallModal
                isOpen={uninstallModalOpen}
                onClose={() => setUninstallModalOpen(false)}
            />
        </div>
    );
}
